import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Crown, 
  CheckCircle2,
  Loader2,
  XCircle,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [attempts, setAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const res = await axios.get(`${API}/subscription/status/${sessionId}`);
      
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser(); // Update user's premium status
        return;
      } else if (res.data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setTimeout(() => {
        setAttempts(prev => prev + 1);
        pollPaymentStatus();
      }, pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setTimeout(() => {
        setAttempts(prev => prev + 1);
        pollPaymentStatus();
      }, pollInterval);
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-2xl mx-auto min-h-[60vh] flex items-center justify-center" data-testid="subscription-success-page">
        <div className="text-center w-full">
          {status === 'loading' && (
            <div className="animate-fade-in">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
              <h1 className="font-serif text-2xl lg:text-3xl font-bold text-foreground mb-3">
                Processing Your Payment...
              </h1>
              <p className="text-muted-foreground">
                Please wait while we confirm your subscription.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="animate-fade-in">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full gold-gradient mb-6 animate-pulse-gold">
                <Crown className="w-12 h-12 text-purple-950" />
              </div>
              <h1 className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-3">
                Welcome to Crown Premium!
              </h1>
              <p className="text-muted-foreground mb-8">
                Your payment was successful. All premium features are now unlocked.
              </p>
              
              <div className="crown-card p-6 mb-8 text-left max-w-md mx-auto">
                <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  You Now Have Access To:
                </h2>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-foreground">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Full AI-powered "Adjust My Crown" support
                  </li>
                  <li className="flex items-center gap-2 text-foreground">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    All healing modules and lessons
                  </li>
                  <li className="flex items-center gap-2 text-foreground">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Advanced no-contact tracker
                  </li>
                  <li className="flex items-center gap-2 text-foreground">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Sisterhood community access
                  </li>
                </ul>
              </div>

              <Link to="/dashboard">
                <Button className="btn-gold rounded-full px-8 py-6 text-lg">
                  Go to Dashboard
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {status === 'failed' && (
            <div className="animate-fade-in">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-destructive/10 mb-6">
                <XCircle className="w-10 h-10 text-destructive" />
              </div>
              <h1 className="font-serif text-2xl lg:text-3xl font-bold text-foreground mb-3">
                Payment Not Completed
              </h1>
              <p className="text-muted-foreground mb-8">
                We couldn't confirm your payment. If you believe this is an error, please contact support.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/subscription">
                  <Button className="rounded-full px-8 py-5 bg-primary hover:bg-primary/90">
                    Try Again
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="outline" className="rounded-full px-8 py-5">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
